import React from 'react';
import { Court } from '@court-sync/shared';
import { Button, Tooltip } from '@/components/ui';

export type CourtCardLayout = 'default' | 'compact';

export type CourtCardProps = {
  court: Court;
  hasValidSubscription: boolean;
  tooltipMessage: string;
  submitting: boolean;
  onEdit: (court: Court) => void;
  onDuplicate: (court: Court) => void;
  onDelete: (court: Court) => void;
  /** default: grouped / unassigned; compact: paginated list row */
  layout?: CourtCardLayout;
};

function capitalizeSurfaceOrType(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function CourtCard({
  court,
  hasValidSubscription,
  tooltipMessage,
  submitting,
  onEdit,
  onDuplicate,
  onDelete,
  layout = 'default',
}: CourtCardProps) {
  const titleRowClass =
    layout === 'compact' ? 'flex items-center space-x-3' : 'flex flex-wrap items-center gap-3';

  return (
    <div className="rounded-xl border border-border bg-card text-card-foreground shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className={titleRowClass}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{court.name}</h3>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                court.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}
            >
              {court.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm text-gray-600 dark:text-gray-400">
            {court.surfaceType && (
              <div>
                <span className="font-medium">Surface:</span> {capitalizeSurfaceOrType(court.surfaceType)}
              </div>
            )}
            {court.courtType && (
              <div>
                <span className="font-medium">Type:</span> {capitalizeSurfaceOrType(court.courtType)}
              </div>
            )}
            <div>
              <span className="font-medium">Created:</span> {new Date(court.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button size="sm" variant="outline" onClick={() => onEdit(court)} disabled={!hasValidSubscription}>
              Edit
            </Button>
          </Tooltip>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDuplicate(court)}
              disabled={submitting || !hasValidSubscription}
              className="text-blue-600 hover:text-blue-700 hover:border-blue-300"
            >
              Duplicate
            </Button>
          </Tooltip>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(court)}
              disabled={!hasValidSubscription}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
